/**
 * Fuwa Market - Paid gift wrapping add-on
 * ------------------------------------------------------------------
 * Adds the "ギフトラッピング" product (variant 49155914498202) as a
 * normal, separate cart line item.
 *
 * Multi-wrapping:
 *  - 1 wrapping = 220 JPY. Buy as many as needed.
 *  - Product page: ticking the checkbox reveals a small quantity
 *    stepper; that quantity is added on "add to cart".
 *  - /cart/add.js accumulates; the wrapping line is edited / removed
 *    in the cart like any other product.
 *
 * Order note ("ご注文についての備考") - free text: gift wrapping combos,
 * delivery-date requests, anything about the order:
 *  - Still saved as the standard Shopify cart note via /cart/update.js
 *    (UNCHANGED) -> admin order "Note".
 *  - ALSO mirrored VERBATIM onto the gift wrapping line item as the
 *    customer-visible property "ご注文についての備考" so the buyer sees it
 *    on the thank-you page, order confirmation email and (new) customer
 *    account order page. No label text is prepended to the value. Only
 *    while a wrapping line is in the cart.
 *  - The mirror always reads the current cart first (never a stale
 *    line key) and passes quantity explicitly (a bare property change
 *    would reset quantity to 1).
 *  - Every wrapping add carries the same property so the line never
 *    splits into "with" + "without" property; syncWrapping() merges
 *    any split and rebuilds the line when clearing the property
 *    (properties:{} via /cart/change.js does NOT remove it).
 *  - CHECKOUT GUARD: if the buyer presses "ご購入手続きへ" before the
 *    mirror has finished, the checkout navigation is held until the
 *    property is saved (capped at 5s), then it proceeds to the
 *    standard Shopify checkout. Fast operators never reach checkout
 *    without the property.
 * ------------------------------------------------------------------
 */
(function () {
  'use strict';

  var GIFT_WRAPPING_VARIANT_ID = 49155914498202;
  var SECTIONS = 'main-cart,cart-drawer,cart-bubble';
  var NOTE_ID = 'CartSpecialInstructions';
  var NOTE_PROP = '_ご注文についての備考'; // Fuwa 2026-10-11: 「_」付きにして管理画面・チェックアウトに出さない（注文のメモ1か所で見る）
  var LEGACY_PROP = 'ラッピングのご希望'; // pre-rename key: cleaned off any cart still carrying it
  var SYNC_CAP_MS = 5000;

  var noteValue = null;
  var noteTimer = null;
  var lastSyncedNote = null;   // note text last confirmed mirrored
  var checkoutPending = false; // a checkout flush is already running

  // --- serialized sync queue: never run two syncWrapping() at once, always
  //     converge on the latest note text (needed because the "clear" path
  //     removes + re-adds the line and is not concurrency-safe) ---
  var syncTail = Promise.resolve();
  var pendingText;
  var pendingActive = false;
  var syncBusy = 0;

  function enqueueSync(text) {
    pendingText = text;
    pendingActive = true;
    syncTail = syncTail.then(function () {
      if (!pendingActive) return;
      var t = pendingText;
      pendingActive = false;
      syncBusy++;
      return syncWrapping(t).then(function (res) {
        lastSyncedNote = t;
        return res;
      }).catch(function (err) {
        console.error('[gift-wrapping] property sync failed', err);
      }).then(function (res) {
        syncBusy--;
        return res;
      });
    });
    return syncTail;
  }
  function syncQuiescent() {
    return syncBusy === 0 && !pendingActive;
  }

  function routes() { return (window.theme && window.theme.routes) || {}; }
  function u(name, fallback) { return routes()[name] || fallback; }

  function post(endpoint, data) {
    return fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(data),
      keepalive: true
    }).then(function (r) { return r.json(); });
  }
  function getCart() {
    return fetch(u('cart_url', '/cart') + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }
  function refreshCartUI() {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }
  function checkoutUrl() {
    var root = (routes().root_url || '/').replace(/\/+$/, '');
    return root + '/checkout';
  }

  function findWrappingLines(cart) {
    var items = (cart && cart.items) ? cart.items : [];
    return items.filter(function (it) {
      return it.variant_id === GIFT_WRAPPING_VARIANT_ID || it.id === GIFT_WRAPPING_VARIANT_ID;
    });
  }
  function trimText(t) { return (t == null ? '' : String(t)).trim(); }
  function buildProps(text) { var o = {}; o[NOTE_PROP] = text; return o; }
  function propOf(properties) {
    if (!properties) return '';
    return properties[NOTE_PROP] != null ? String(properties[NOTE_PROP]) : '';
  }
  function hasLegacyProp(properties) {
    return !!(properties && properties[LEGACY_PROP] != null);
  }
  function hasWrappingLineInDom() {
    return !!document.querySelector('[data-id^="' + GIFT_WRAPPING_VARIANT_ID + ':"]');
  }

  function addWrappingItem(quantity, text) {
    var item = { id: GIFT_WRAPPING_VARIANT_ID, quantity: quantity };
    if (trimText(text)) item.properties = buildProps(text);
    return post(u('cart_add_url', '/cart/add') + '.js', {
      items: [item], sections: SECTIONS, sections_url: window.location.pathname
    });
  }

  /**
   * Guarantee exactly ONE gift wrapping line whose "ご注文についての備考" property
   * equals `text` verbatim (or carries no such property when `text` is blank),
   * keeping the combined quantity. Reads the current cart first so the key is
   * fresh. A /cart/change.js properties update REPLACES the property set, so a
   * pre-rename "ラッピングのご希望" key is dropped automatically on the next edit;
   * hasLegacyProp() forces a sync when only that stale key is present.
   * Resolves with the last cart response, or null when nothing had to change.
   */
  function syncWrapping(text) {
    var want = trimText(text) ? text : '';
    return getCart().then(function (cart) {
      var lines = findWrappingLines(cart);
      if (lines.length === 0) return null;

      var single = lines.length === 1;
      if (single && propOf(lines[0].properties) === want && !hasLegacyProp(lines[0].properties)) return null;

      if (single && want) {
        return post(u('cart_change_url', '/cart/change') + '.js', {
          id: lines[0].key,
          quantity: lines[0].quantity, // MUST send, else quantity resets to 1
          properties: buildProps(text),
          sections: SECTIONS,
          sections_url: window.location.pathname
        });
      }

      // clearing the property, or merging a split -> rebuild the wrapping line
      var total = lines.reduce(function (s, l) { return s + l.quantity; }, 0);
      var chain = Promise.resolve();
      lines.forEach(function (l) {
        chain = chain.then(function () {
          return post(u('cart_change_url', '/cart/change') + '.js', { id: l.key, quantity: 0 });
        });
      });
      return chain.then(function () { return addWrappingItem(total, want); });
    });
  }

  // cart.note (unchanged) + mirror to the wrapping line property
  function saveNote(value, doRefresh) {
    noteValue = value;
    // Fuwa: カートのギフト欄（kuro-gift-options.js）の追加の詳細も同じ1回で保存する
    // （備考と追加の詳細を別々に同時に送ると、片方が消えることがあるため）
    var body = { note: value };
    var kuroAttrs = window.KuroGift && window.KuroGift.attributes ? window.KuroGift.attributes() : null;
    if (kuroAttrs) body.attributes = kuroAttrs;
    var noteSaved = post(u('cart_update_url', '/cart/update') + '.js', body);
    var mirrored = enqueueSync(value).then(function (res) {
      if (res && doRefresh) refreshCartUI();
    });
    return Promise.all([noteSaved, mirrored]);
  }

  /* ---------- product page: reveal stepper only while checked ---------- */
  document.addEventListener('change', function (event) {
    var checkbox = event.target.closest('[data-gift-wrapping-checkbox]');
    if (!checkbox) return;
    var option = checkbox.closest('[data-gift-wrapping-option]');
    if (!option) return;
    var qtyWrap = option.querySelector('[data-gift-wrapping-qty-wrap]');
    var qtyInput = option.querySelector('[data-gift-wrapping-qty]');
    if (!qtyWrap) return;
    qtyWrap.hidden = !checkbox.checked;
    if (qtyInput) qtyInput.value = 1;
  });

  /* ---------- add wrapping (product-form hook + cart/drawer prompt) ---------- */
  function addToCart(quantity) {
    var qty = parseInt(quantity, 10);
    if (isNaN(qty) || qty < 1) qty = 1;
    return getCart().then(function (cart) {
      var text = cart.note;
      return addWrappingItem(qty, text).then(function (addResp) {
        return enqueueSync(text).then(function (res) {
          return (res && res.sections) ? res : addResp;
        });
      });
    });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-gift-wrapping-add]');
    if (!button) return;
    event.preventDefault();
    if (button.getAttribute('aria-disabled') === 'true') return;
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('is-loading');
    addToCart(1).then(function () { refreshCartUI(); }).catch(function (err) {
      console.error('[gift-wrapping]', err);
      button.removeAttribute('aria-disabled');
      button.classList.remove('is-loading');
    });
  });

  /* ---------- order note field ---------- */
  document.addEventListener('input', function (event) {
    if (!event.target || event.target.id !== NOTE_ID) return;
    var value = event.target.value;
    noteValue = value;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(function () { saveNote(value, false); }, 400);
  });
  document.addEventListener('focusout', function (event) {
    if (!event.target || event.target.id !== NOTE_ID) return;
    clearTimeout(noteTimer);
    saveNote(event.target.value, true);
  });
  document.addEventListener('line-item:change:end', function () {
    if (noteValue == null) return;
    var field = document.getElementById(NOTE_ID);
    if (field && field.value === '' && noteValue !== '') {
      field.value = noteValue;
      saveNote(noteValue, false);
    }
  });

  /* The cart page's "ご注文についての備考" block is rendered by Liquid only when
     a gift wrapping line is present. A cart-drawer quantity change does NOT
     re-render the cart page section, so keep the row's visibility in sync when
     wrapping is removed / re-added while the cart page is open. */
  function syncNoteRowVisibility() {
    var row = document.querySelector('.cart-note-row');
    if (!row) return; // Liquid already gated it out (or not the cart page)
    getCart().then(function (cart) {
      var hasGiftSet = (cart.items || []).some(function (item) {
        return String(item.handle || '').indexOf('gift-set-') === 0;
      });
      row.style.display = (findWrappingLines(cart).length > 0 || hasGiftSet) ? '' : 'none';
    }).catch(function () {});
  }
  document.addEventListener('line-item:change:end', syncNoteRowVisibility);
  document.addEventListener('cart:item-added', syncNoteRowVisibility);

  /* ---------- checkout guard ---------- */
  function noteIsSynced() {
    var field = document.getElementById(NOTE_ID);
    if (!field) return true;               // note field not present on this page
    if (!hasWrappingLineInDom()) return true; // no wrapping line -> nothing to mirror
    return syncQuiescent() && lastSyncedNote === field.value;
  }
  function isCheckoutSubmit(event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return false;
    if (event.submitter && event.submitter.name === 'checkout') return true;
    if (form.querySelector('[name="checkout"]')) return true;
    if (form.id && document.querySelector('[name="checkout"][form="' + form.id + '"]')) return true;
    return false;
  }
  document.addEventListener('submit', function (event) {
    if (!isCheckoutSubmit(event)) return;
    if (checkoutPending) { event.preventDefault(); return; } // flush already running
    if (noteIsSynced()) return;                              // property already up to date -> proceed normally

    event.preventDefault();
    checkoutPending = true;

    var btn = event.submitter || document.querySelector('[name="checkout"]');
    if (btn) { btn.setAttribute('aria-disabled', 'true'); btn.classList.add('is-loading'); }

    var field = document.getElementById(NOTE_ID);
    var flush = saveNote(field ? field.value : (noteValue || ''), false);
    var cap = new Promise(function (res) { setTimeout(res, SYNC_CAP_MS); });
    Promise.race([flush.catch(function () {}), cap]).then(function () {
      // property is now saved server-side -> continue to the standard Shopify checkout
      window.location.href = checkoutUrl();
    });
  }, true);

  window.GiftWrapping = {
    variantId: GIFT_WRAPPING_VARIANT_ID,
    noteProperty: NOTE_PROP,
    addToCart: addToCart
  };
})();
